/**
 * A staff member in the record panel. There is no page for one; the facts
 * are the record, and the role and removal stay on the Staff list's rows
 * until module 4 makes them panel forms.
 */
import { useQuery } from '@tanstack/react-query';

import { listPlatformStaff } from '@optum/api-client/endpoints/platform-staff';

import type { RecordRef } from '../../components/shell/record.js';
import {
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordListError,
  RecordMissing,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { LoadingBlock } from '../../ui/AsyncState.js';
import { absoluteDate, relativeTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { Avatar, Typography } from '../../ui/finixt.js';
import { StatusBadge } from '../../ui/StatusBadge.js';

export function StaffRecord({ record }: { record: RecordRef }) {
  const staff = useQuery({ queryKey: qk.platformStaff(), queryFn: listPlatformStaff });
  if (staff.isPending) return <LoadingBlock />;
  if (staff.isError) {
    return (
      <RecordListError
        message={staff.error instanceof Error ? staff.error.message : 'Failed to load'}
      />
    );
  }
  const s = staff.data.find((row) => row.id === record.id);
  if (!s) return <RecordMissing record={record} />;
  const initials = (s.email.split('@')[0] ?? '?').slice(0, 2).toUpperCase();

  return (
    <RecordColumn>
      <RecordIdentity
        avatar={
          <Avatar
            initials={initials}
            size={40}
            shape="round"
            tint="var(--info-tint)"
            color="var(--info)"
          />
        }
        eyebrow={
          <>
            <Typography variant="eyebrow">Staff</Typography>
            <StatusBadge status={s.role} vocabulary="platformRole" />
          </>
        }
        name={s.email}
        subtitle={s.idp_name}
      />
      <RecordSection label="Facts">
        <RecordFacts
          items={[
            { label: 'Role', value: s.role },
            { label: 'Status', value: s.status ?? '—', mono: false },
            { label: 'Added', value: absoluteDate(s.created_at) },
            { label: 'Last login', value: s.last_login_at ? relativeTime(s.last_login_at) : 'never' },
          ]}
        />
      </RecordSection>
    </RecordColumn>
  );
}
